import React, { useRef } from 'react';
import { gsap } from 'gsap';

import { MASK_VARIABLES } from './constants';

import { useAnimationEffect, AnimationEnabledBreakpoints } from 'animations';
import {
  Coordinate,
  findLargestDistanceFromCenter,
  getDistanceFromPageLeft,
  getDistanceFromPageTop,
} from 'animations/helpers';

export const useMaskCircle = (
  textRefs: React.RefObject<HTMLDivElement>[],
  enabledBreakpoint: AnimationEnabledBreakpoints = 0
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useAnimationEffect(
    ({ enabled }) => {
      if (enabled && contentRef.current && containerRef.current) {
        const circleOffsetTop = getDistanceFromPageTop(contentRef.current);
        const circleHeight = contentRef.current.offsetHeight;
        const circleOffsetLeft = getDistanceFromPageLeft(contentRef.current);
        const circleWidth = contentRef.current.offsetWidth;

        const circleCenter: Coordinate = {
          x: circleOffsetLeft + circleWidth / 2,
          y: circleOffsetTop + circleHeight / 2,
        };

        const containerOffsetTop = getDistanceFromPageTop(containerRef.current);
        const containerHeight = containerRef.current.offsetHeight;
        const containerOffsetBottom = containerOffsetTop + containerHeight;

        const containerOffsetLeft = getDistanceFromPageLeft(containerRef.current);
        const containerOffsetRight = containerOffsetLeft + containerRef.current.offsetWidth;

        const containerTopLeft: Coordinate = {
          x: containerOffsetLeft,
          y: containerOffsetTop,
        };
        const containerTopRight: Coordinate = {
          x: containerOffsetRight,
          y: containerOffsetTop,
        };

        const containerBottomLeft: Coordinate = {
          x: containerOffsetLeft,
          y: containerOffsetBottom,
        };
        const containerBottomRight: Coordinate = {
          x: containerOffsetRight,
          y: containerOffsetBottom,
        };

        const maximumRadius = findLargestDistanceFromCenter(
          [containerTopLeft, containerTopRight, containerBottomLeft, containerBottomRight],
          circleCenter
        );

        const maximumScale = maximumRadius / (circleHeight / 2);

        gsap.fromTo(
          contentRef.current,
          { [MASK_VARIABLES.CIRCLE_SCALE]: 1 },
          {
            scrollTrigger: {
              trigger: contentRef.current,
              start: 'center 75%',
              endTrigger: containerRef.current,
              end: `bottom top`,
              scrub: true,
            },
            [MASK_VARIABLES.CIRCLE_SCALE]: maximumScale,
          }
        );

        textRefs.forEach(ref => {
          if (ref.current) {
            const textOffsetTop = getDistanceFromPageTop(ref.current);
            const textOffsetLeft = getDistanceFromPageLeft(ref.current);

            const coordinateVariables = {
              [MASK_VARIABLES.TEXT_MASK_X]: `${circleCenter.x - textOffsetLeft}px`,
              [MASK_VARIABLES.TEXT_MASK_Y]: `${circleCenter.y - textOffsetTop}px`,
            };

            gsap.fromTo(ref.current, coordinateVariables, coordinateVariables);
          }
        });
      }
    },
    [textRefs],
    enabledBreakpoint
  );

  return { containerRef, contentRef };
};
